/**
 * useProfileImageUpload - Hook for handling profile image uploads
 * 
 * Provides functionality to upload profile images with loading states,
 * error handling, and automatic profile refresh after successful upload.
 */

import { useQueryClient } from '@tanstack/react-query';
import { imagesApi, type ImagesUploadResponse } from '../lib/api';
import { useOroMutation, OroOperation } from './useOroMutation';

interface UseProfileImageUploadResult {
  uploadImage: (file: File) => Promise<void>;
  isUploading: boolean;
  error: string | null;
  reset: () => void;
}

export const useProfileImageUpload = (): UseProfileImageUploadResult => {
  const queryClient = useQueryClient();

  const mutation = useOroMutation<ImagesUploadResponse, File>({
    mutationFn: async (file: File) => {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }

      // Validate file size (20MB limit - server optimizes via CF Image Resizing)
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        throw new Error('Image must be smaller than 20MB');
      }

      // Unified images API (#1068); throws ApiError on a non-2xx response.
      return imagesApi.upload(file, 'profile');
    },
    onSuccess: () => {
      // Refresh profile data to show updated image
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    oro: {
      op: OroOperation.UploadProfileImage,
      retryPolicy: 'never',
      debugLabel: 'Profile image upload',
    },
  });

  return {
    uploadImage: async (file: File) => {
      await mutation.mutateAsync(file);
    },
    isUploading: mutation.isPending,
    error: mutation.error?.message || null,
    reset: mutation.reset,
  };
};
